/**
 * GovernanceWorkspaceStatusBar — Operational status indicators
 *
 * proofOnly status, disclosure profile, stream health, replay availability,
 * continuity state, fixture/live indicator.
 *
 * REFINEMENTS (v1.1):
 * - Calmer truth-surface language
 * - Grouped indicators with clearer hierarchy
 * - No marketing language
 */

import React from 'react';
import type { OperationalWorkspaceState } from '../modules/governanceEventStream/operationalRendering/types.js';
import type { SourceStateIndicator } from './ingestion/index.js';
import { continuityToColor, NEUTRAL, TYPOGRAPHY, SPACING, SEMANTIC } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface StatusBarProps {
  workspace: OperationalWorkspaceState;
  isFixture: boolean;
  sourceState?: SourceStateIndicator | null;
}

export const GovernanceWorkspaceStatusBar: React.FC<StatusBarProps> = ({ workspace, isFixture, sourceState }) => {
  const continuityColor = continuityToColor(workspace.panes.continuity.currentStatus);
  const hasRefusals = workspace.panes.timeline.refusalCount > 0;
  const isStabilized = workspace.panes.continuity.currentStatus === 'stabilized';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.sp4,
      padding: `${SPACING.sp2}px ${SPACING.sp4}px`,
      backgroundColor: NEUTRAL.bg200,
      borderTop: `1px solid ${NEUTRAL.border}`,
      fontSize: TYPOGRAPHY.label.size,
      color: NEUTRAL.text400,
      minHeight: 28,
    }}>
      {/* Stream identity */}
      <span style={{ fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text300 }}>
        {workspace.streamId.slice(0, 16)}…
      </span>

      <div style={{ width: 1, height: 14, backgroundColor: NEUTRAL.borderSubtle }} />

      {/* Continuity state - enhanced with storytelling */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp2 }}>
        <div style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: continuityColor.text,
          boxShadow: isStabilized ? `0 0 8px ${continuityColor.text}60` : `0 0 6px ${continuityColor.text}40`,
        }} />
        <span style={{
          color: continuityColor.text,
          backgroundColor: continuityColor.chip,
          padding: '1px 6px',
          borderRadius: 3,
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: TYPOGRAPHY.label.weight,
          border: `1px solid ${continuityColor.border}`,
        }}>
          {workspace.panes.continuity.currentStatus}
        </span>
        {isStabilized && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: '#5fa35f', opacity: 0.8 }}>
            ✓
          </span>
        )}
      </div>

      <div style={{ width: 1, height: 14, backgroundColor: NEUTRAL.borderSubtle }} />

      {/* Event counts */}
      <span style={{ color: NEUTRAL.text300 }}>
        {workspace.panes.timeline.totalRows} events
      </span>

      {/* Refusal count */}
      {hasRefusals && (
        <span style={{ color: '#e03030', fontWeight: 600 }}>
          {workspace.panes.timeline.refusalCount} refused
        </span>
      )}

      {/* Replay availability */}
      {workspace.panes.replayCompare.windowId && (
        <span style={{ color: SEMANTIC.replayWindow.text }}>
          replay: {workspace.panes.replayCompare.totalInWindow} events
        </span>
      )}

      {/* Admissibility summary */}
      {workspace.panes.admissibilityChain.totalEntries > 0 && (
        <span style={{ color: NEUTRAL.text300 }}>
          {workspace.panes.admissibilityChain.passedCount} passed · {workspace.panes.admissibilityChain.refusedCount} refused
        </span>
      )}

      {/* Source State Indicator */}
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: SPACING.sp2 }}>
        {sourceState ? (
          <>
            <div style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: sourceState.color === 'green' ? '#22c55e' :
                            sourceState.color === 'amber' ? '#f59e0b' :
                            sourceState.color === 'red' ? '#ef4444' :
                            '#3b82f6',
              opacity: sourceState.state === 'live' ? 1 : 0.7,
            }} />
            <span
              title={sourceState.error || sourceState.label}
              style={{
                color: sourceState.color === 'green' ? '#5fa35f' :
                      sourceState.color === 'amber' ? '#f59e0b' :
                      sourceState.color === 'red' ? '#e06060' :
                      '#7ba3d1',
                backgroundColor: sourceState.color === 'green' ? '#0a1a0a' :
                                  sourceState.color === 'amber' ? '#1a1505' :
                                  sourceState.color === 'red' ? '#1a0a0a' :
                                  '#0a0a1a',
                padding: '1px 6px',
                borderRadius: 3,
                fontSize: TYPOGRAPHY.micro.size,
                fontWeight: 600,
                border: `1px solid ${
                  sourceState.color === 'green' ? '#2a4a2a' :
                  sourceState.color === 'amber' ? '#4a3a10' :
                  sourceState.color === 'red' ? '#4a2020' :
                  '#1a2a4a'
                }`,
              }}
            >
              {sourceState.state}
            </span>
            {sourceState.state === 'fail-closed' && (
              <span style={{ fontSize: TYPOGRAPHY.micro.size, color: '#a05050' }}>
                (blocked)
              </span>
            )}
          </>
        ) : (
          // Fallback to simple fixture/live indicator if no sourceState provided
          <span style={{
            color: isFixture ? '#c89010' : '#5fa35f',
            backgroundColor: isFixture ? '#251a00' : 'transparent',
            padding: '1px 6px',
            borderRadius: 3,
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            border: isFixture ? '1px solid #5a3a00' : 'none',
          }}>
            {isFixture ? 'fixture' : 'live'}
          </span>
        )}
      </span>
    </div>
  );
};
